import { useQuery } from 'react-query'
import { 
  ChartBarIcon, 
  CheckIcon, 
  XMarkIcon, 
  ClockIcon,
  UserGroupIcon,
  PhotoIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { imageApi } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { format } from 'date-fns'

const StatsPage = () => {
  const {
    data: stats,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['annotation-stats'],
    imageApi.getStats,
    {
      refetchInterval: 10000, // Refetch every 10 seconds for faster updates
    }
  )

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    color = 'blue',
    subtitle 
  }: {
    title: string
    value: string | number
    icon: any
    color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple'
    subtitle?: string
  }) => {
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500'
    }

    return (
      <div className="card p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </div>
                {subtitle && (
                  <div className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    {subtitle}
                  </div>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="large" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage 
          message="Failed to load statistics. Please try again." 
          onRetry={refetch}
        />
      </div>
    )
  }

  if (!stats) {
    return null
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Annotation Statistics
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Overview of annotation progress and performance metrics.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary flex items-center space-x-2"
          disabled={isLoading}
        >
          <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Total Images */}
        <StatCard
          title="Total Images"
          value={stats.total_images}
          icon={PhotoIcon}
          color="blue"
        />

        {/* Annotated Images */}
        <StatCard
          title="Annotated Images"
          value={stats.annotated_images}
          icon={ChartBarIcon}
          color="purple"
          subtitle={`${stats.completion_percentage}% complete`}
        />

        {/* Pending Images */}
        <StatCard
          title="Pending Review"
          value={stats.pending_images}
          icon={ClockIcon}
          color="yellow"
        />

        {/* Accepted Images */}
        <StatCard
          title="Accepted"
          value={stats.accepted_images}
          icon={CheckIcon}
          color="green"
        />

        {/* Rejected Images */}
        <StatCard
          title="Rejected"
          value={stats.rejected_images}
          icon={XMarkIcon}
          color="red"
        />

        {/* Active Annotators */}
        <StatCard
          title="Active Annotators"
          value={stats.annotators_count}
          icon={UserGroupIcon}
          color="blue"
        />
      </div>

      {/* Progress Bar */}
      <div className="mt-8 card p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Annotation Progress
        </h3>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${stats.completion_percentage}%` }}
          >
            <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600" />
          </div>
        </div>
        <div className="mt-2 flex justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>{stats.annotated_images} annotated</span>
          <span>{stats.total_images - stats.annotated_images} remaining</span>
        </div>
      </div>

      {/* Breakdown Chart */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Status Breakdown
          </h3>
          <div className="space-y-4">
            {/* Accepted */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Accepted</span>
              </div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {stats.accepted_images} ({stats.total_images > 0 ? ((stats.accepted_images / stats.total_images) * 100).toFixed(1) : 0}%)
              </div>
            </div>

            {/* Rejected */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Rejected</span>
              </div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {stats.rejected_images} ({stats.total_images > 0 ? ((stats.rejected_images / stats.total_images) * 100).toFixed(1) : 0}%)
              </div>
            </div>

            {/* Pending */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Pending</span>
              </div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {stats.pending_images} ({stats.total_images > 0 ? ((stats.pending_images / stats.total_images) * 100).toFixed(1) : 0}%)
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h3>
          <div className="space-y-3">
            {stats.last_annotation_date ? (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Last annotation: {format(new Date(stats.last_annotation_date), 'PPP')}
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                No annotations yet
              </div>
            )}
            
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total annotators: {stats.annotators_count}
            </div>

            {stats.total_images > 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Completion rate: {stats.completion_percentage}%
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auto-refresh indicator */}
      <div className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
        Statistics auto-refresh every 10 seconds • Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  )
}

export default StatsPage 